import api from './client'

/* ── Types ──────────────────────────────────────────────────────── */

export type IntegrationTrigger = 'add' | 'edit' | 'delete' | 'action'

export interface IntegrationEventSource {
  type: 'transaction' | 'custom'
  obj_name?: string
  trigger?: IntegrationTrigger
  action_name?: string
}

/** Legal types for an event argument (drives the schema synced to IOFlow). */
export type IntegrationArgType =
  | 'string'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'object'
  | 'array'

/** One declared, typed argument of an event — the input contract IOFlow maps to. */
export interface IntegrationEventArg {
  name: string
  type: IntegrationArgType
  description?: string
  required?: boolean
  default?: unknown
  /** Transaction events: the record column the value comes from. Omitted for custom events. */
  source?: string
}

export interface IntegrationEvent {
  event_name: string
  display_name?: string
  description?: string
  source: IntegrationEventSource
  /** Record fields passed in the event payload ([] / missing = all). */
  params?: string[]
  /** Typed argument declarations — the inputs IOFlow receives and maps. */
  arguments?: IntegrationEventArg[]
  payload_schema?: Record<string, unknown> | null
  sample_payload?: Record<string, unknown> | null
  active: boolean
}

/** Operations IOFlow flows can call ON the application (inbound APIs). */
export type IntegrationApiOperation =
  | 'add_record'
  | 'update_record'
  | 'get_records'
  | 'run_action'
  | 'bulk_upsert'

/** Legacy outbound API kinds (no longer authorable — kept for older designs). */
export type IntegrationApiCategory = 'email' | 'whatsapp' | 'telegram' | 'http' | 'custom'

export interface IntegrationApi {
  api_name: string
  display_name?: string
  description?: string
  /** Inbound operation; set on every API authored in the current designer. */
  operation?: IntegrationApiOperation
  /** Transaction object the operation targets. */
  obj_name?: string
  /** Action button to run (operation = run_action). */
  action_name?: string
  /** Record fields the call accepts ([] / missing = all enterable fields). */
  args?: string[]
  /** Legacy outbound kind — entries with a category and no operation. */
  category?: IntegrationApiCategory
  input_schema?: Record<string, unknown> | null
  output_schema?: Record<string, unknown> | null
  sync?: boolean
  active: boolean
  /** Set on APIs auto-migrated from a raw-URL Call API definition. */
  migrated_from?: string
}

export interface IntegrationIOFlowState {
  app_id?: string
  url?: string
  event_ids?: Record<string, string>
  api_event_ids?: Record<string, string>
  service_ids?: Record<string, string>
  last_synced_at?: string
}

export interface IntegrationDesign {
  version: number
  events: IntegrationEvent[]
  apis: IntegrationApi[]
  ioflow: IntegrationIOFlowState
}

export interface SyncReport {
  success: boolean
  message?: string
  warning?: string
  app_id?: string
  app_name?: string
  events_synced?: string[]
  apis_synced?: string[]
  inbound_apis_synced?: string[]
  has_invocation_key?: boolean
  last_synced_at?: string
  migration?: {
    migrated: { url: string; api_name: string }[]
    rewritten_files: string[]
    skipped: { file: string; reason: string }[]
  }
  /** Transaction models whose follow-up actions were updated to mirror events. */
  follow_ups?: { updated_models: string[] }
}

export interface IntegrationStatus {
  success: boolean
  synced: boolean
  app_id?: string
  last_synced_at?: string
  ioflow_frontend_url: string
  event_count: number
  api_count: number
  /** Endpoint template inbound APIs are published under ('' = not configured). */
  inbound_call_url_template?: string
  flows_by_event: Record<string, { flow_id: string; flow_name: string; status: string }[]>
  flows_error?: string
}

/* ── API functions ──────────────────────────────────────────────── */

export async function getIntegrationDesign(project: string): Promise<IntegrationDesign> {
  const { data } = await api.get(`/integration/design/${encodeURIComponent(project)}`)
  return data.design as IntegrationDesign
}

export async function saveIntegrationDesign(project: string, design: IntegrationDesign) {
  const { data } = await api.post(`/integration/design/${encodeURIComponent(project)}`, { design })
  return data as { success: boolean; message: string; updated_models?: string[] }
}

export async function syncToIOFlow(project: string): Promise<SyncReport> {
  const { data } = await api.post(`/integration/sync/${encodeURIComponent(project)}`)
  return data as SyncReport
}

export async function getIntegrationStatus(
  project: string,
  includeFlows = false,
): Promise<IntegrationStatus> {
  const { data } = await api.get(`/integration/status/${encodeURIComponent(project)}`, {
    params: { include_flows: includeFlows },
  })
  return data as IntegrationStatus
}

export async function getOpenInIOFlowUrl(
  project: string,
  target: 'application' | 'flows' | 'flow' | 'executions' = 'application',
  flowId = '',
): Promise<string> {
  const { data } = await api.get(`/integration/open-url/${encodeURIComponent(project)}`, {
    params: { target, flow_id: flowId },
  })
  return data.url as string
}

/* ── Flows tied to the project (read live from IOFlow) ─────────── */

export type ProjectFlowUseKind = 'event' | 'api' | 'scheduler' | 'menu' | 'built'

/** One metered quantity — e.g. 1,292 posts read, 88,886 output tokens. */
export interface FlowUsageMeter {
  meter: string
  label: string
  unit: string
  quantity: number
  /** null = no price known for it (counted, not costed). */
  cost: number | null
}

export interface ProjectFlowRun {
  execution_id?: string
  status: string
  trigger_type: string
  started_at?: string | null
  finished_at?: string | null
  duration_seconds?: number | null
  error: string
  /** What the run's paid API calls cost; null = nothing metered for it. */
  cost?: number | null
  cost_meters?: FlowUsageMeter[]
}

/** Where a flow's cost comes from: one step × meter (× model). */
export interface FlowUsageStep {
  node_id: string
  label: string
  app_name?: string | null
  service_name?: string | null
  meter: string
  meter_label: string
  unit: string
  /** What the price depended on, e.g. the model that answered. */
  dimension: string
  quantity: number
  cost: number | null
  calls_metered: number
}

/** A flow's daily budget, enforced by IOFlow (UTC days). */
export interface FlowBudget {
  daily: number
  currency: string
  spent_today?: number
  reached?: boolean
}

/** IOFlow's usage & cost summary of one flow (estimated from each answer). */
export interface FlowUsage {
  currency: string
  days: number
  since: string
  today: string
  total_cost: number
  today_cost: number
  last_7_days_cost: number
  has_unpriced: boolean
  budget: FlowBudget | null
  by_day: { day: string; cost: number; unpriced: number }[]
  by_step: FlowUsageStep[]
  runs: { execution_id: string; started_at: string; cost: number; meters: FlowUsageMeter[] }[]
  basis: string
  price_notes: { app_name?: string | null; note: string }[]
}

export interface ProjectFlow {
  flow_id: string
  name: string
  description: string
  status: string
  active: boolean
  trigger_type: string
  scheduled: boolean
  /** Plain-words cadence, e.g. "Every 10 minutes" ('' when not scheduled). */
  schedule_text: string
  next_run?: string | null
  /** events = listens to this app, apis = calls it, both, owned = the project's own flow. */
  direction: 'events' | 'apis' | 'both' | 'owned'
  uses: { kind: ProjectFlowUseKind; name: string }[]
  runs?: ProjectFlowRun[]
  runs_error?: string
  /** null with usage_error set = IOFlow could not say (never "no cost"). */
  usage?: FlowUsage | null
  usage_error?: string
}

export interface ProjectFlowsResponse {
  success: boolean
  reachable: boolean
  synced: boolean
  error?: string
  flows: ProjectFlow[]
}

export async function getProjectFlows(project: string, runs = 12): Promise<ProjectFlowsResponse> {
  const { data } = await api.get(`/integration/flows/${encodeURIComponent(project)}`, {
    params: { runs },
  })
  return data as ProjectFlowsResponse
}

export async function setProjectFlowActive(
  project: string,
  flowId: string,
  active: boolean,
): Promise<ProjectFlow> {
  const { data } = await api.post(`/integration/flow-state/${encodeURIComponent(project)}`, {
    flow_id: flowId,
    active,
  })
  return data.flow as ProjectFlow
}

/** Set (or remove, with null) a flow's daily budget; IOFlow enforces it. */
export async function setProjectFlowBudget(
  project: string,
  flowId: string,
  daily: number | null,
): Promise<FlowBudget | null> {
  const { data } = await api.post(`/integration/flow-budget/${encodeURIComponent(project)}`, {
    flow_id: flowId,
    daily,
  })
  return (data?.budget ?? null) as FlowBudget | null
}

export async function generateIntegrationDesign(project: string, description: string) {
  const { data } = await api.post(`/integration/generate/${encodeURIComponent(project)}`, {
    description,
  })
  return data as { success: boolean; events: IntegrationEvent[]; apis: IntegrationApi[] }
}

/* ── Connecting an app / publishing a connector / references ──────── */

/** One IOFlow application and whether this tenant can already use it. */
export interface ConnectableApp {
  app_id: string
  app_name: string
  category?: string
  auth_type?: string
  connected: boolean
  /** OAuth2: cannot be connected from a form — needs the browser. */
  needs_browser: boolean
}

/** One field the connect form must render, derived from the app's auth type. */
export interface ConnectField {
  name: string
  label: string
  secret: boolean
  required: boolean
  default?: string
}

export interface ConnectFieldsResponse {
  ok: boolean
  app_id: string
  app_name: string
  auth_type: string
  needs_browser: boolean
  fields: ConnectField[]
}

export async function listConnectableApps(project: string): Promise<ConnectableApp[]> {
  const { data } = await api.get(`/integration/connectable/${encodeURIComponent(project)}`)
  return (data.applications || []) as ConnectableApp[]
}

export async function getConnectFields(
  project: string,
  appId: string,
): Promise<ConnectFieldsResponse> {
  const { data } = await api.get(`/integration/connect-fields/${encodeURIComponent(project)}`, {
    params: { app_id: appId },
  })
  return data as ConnectFieldsResponse
}

/**
 * Connect an IOFlow application with credentials the user typed.
 *
 * The credentials go straight through to IOFlow, which encrypts them. They are
 * never persisted by Twasta and never reach the agent — the caller hands the
 * agent back `agent_reply`, which carries only the subscription id.
 */
export async function connectIOFlowApp(
  project: string,
  appId: string,
  credentials: Record<string, string>,
): Promise<{ ok: boolean; subscription_id: string; agent_reply: string }> {
  const { data } = await api.post(`/integration/connect/${encodeURIComponent(project)}`, {
    app_id: appId,
    auth_credentials: credentials,
  })
  return data
}

/**
 * Publish a GLOBAL connector using an instance admin's authorization code.
 * Same rule as above: the code is used once server-side and never returned.
 */
export async function publishIOFlowConnector(
  project: string,
  seedJson: unknown,
  authorizationCode: string,
): Promise<{ ok: boolean; app_key?: string; agent_reply?: string; message?: string }> {
  const { data } = await api.post(`/integration/publish/${encodeURIComponent(project)}`, {
    seed_json: seedJson,
    authorization_code: authorizationCode,
  })
  return data
}

export type IOFlowReferenceKind = 'flow' | 'app' | 'example'

export interface IOFlowReference {
  id: string
  name: string
  category?: string
  status?: string
}

export async function listIOFlowReferences(
  project: string,
  kind: IOFlowReferenceKind = 'flow',
): Promise<IOFlowReference[]> {
  const { data } = await api.get(`/integration/reference/${encodeURIComponent(project)}`, {
    params: { kind },
  })
  return (data.items || []) as IOFlowReference[]
}

export async function getIOFlowReference(
  project: string,
  kind: IOFlowReferenceKind,
  refId: string,
): Promise<unknown> {
  const { data } = await api.get(`/integration/reference/${encodeURIComponent(project)}`, {
    params: { kind, ref_id: refId },
  })
  return data.reference
}
